import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { AuthShell } from "./Login";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim() });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || t("forgot.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("forgot.title")} eyebrow={t("forgot.eyebrow")}>
      {sent ? (
        <div data-testid="forgot-sent-state" className="space-y-5">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-white/5 border border-white/10 mx-auto">
            <Mail className="w-5 h-5 text-white/70" />
          </div>
          <div className="text-center">
            <div className="font-display text-lg font-black tracking-tight mb-2">
              {t("forgot.checkInbox")}
            </div>
            <div className="text-[13px] text-white/70 leading-relaxed">
              {t("forgot.sentBody", { email: email.trim() })}
            </div>
          </div>
          <Link
            to="/login"
            className="btn-ghost focus-ring w-full justify-center"
            data-testid="forgot-back-to-login"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            <span className="text-[11px] uppercase tracking-[0.15em]">
              {t("forgot.backToLogin")}
            </span>
          </Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
          <p className="text-[13px] text-white/70 leading-relaxed">
            {t("forgot.body")}
          </p>
          <div>
            <label htmlFor="fp-email" className="label-eyebrow block mb-2">
              {t("forgot.email")}
            </label>
            <input
              id="fp-email"
              data-testid="forgot-email"
              type="email"
              required
              autoComplete="email"
              className="cmd-input w-full h-11 px-4 rounded-md text-[14px]"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          {error && (
            <div
              className="text-[12px] font-mono-p text-[#ff9c85] border border-[#ff9c85]/30 rounded px-3 py-2 bg-[#ff9c85]/5"
              data-testid="forgot-error"
            >
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={submitting}
            className="btn-pill w-full h-11 justify-center focus-ring"
            data-testid="forgot-submit"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : t("forgot.submit")}
          </button>
          <div className="label-eyebrow text-center">
            <Link to="/login" className="underline hover:text-white" data-testid="forgot-remembered">
              {t("forgot.remembered")}
            </Link>
          </div>
        </form>
      )}
    </AuthShell>
  );
}
